import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const CONTENT_KEYS = [
  { key: 'landing_hero', label: 'Landing Page - Hero' },
  { key: 'about_us', label: 'Quem Somos' },
  { key: 'contact_info', label: 'Fale Conosco' },
  { key: 'donation_info', label: 'Faça uma Doação' }
];

const AdminContentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Acesso negado');
      navigate('/dashboard');
      return;
    }
    fetchContents();
  }, [user]);

  const fetchContents = async () => {
    setLoading(true);
    try {
      const promises = CONTENT_KEYS.map(({ key }) =>
        api.get(`/api/content/${key}`)
      );
      const responses = await Promise.all(promises);
      
      const contentsMap = {};
      responses.forEach((response, index) => {
        contentsMap[CONTENT_KEYS[index].key] = response.data;
      });
      
      setContents(contentsMap);
    } catch (error) {
      toast.error('Erro ao carregar conteúdos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (key) => {
    setSaving(key);
    try {
      await api.put(`/api/content/${key}`, contents[key]);
      toast.success('Conteúdo atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar conteúdo');
    } finally {
      setSaving(null);
    }
  };

  const updateContent = (key, field, value) => {
    setContents(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Gerenciar Conteúdos do Site</h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">Carregando...</div>
        ) : (
          <div className="space-y-6">
            {CONTENT_KEYS.map(({ key, label }) => (
              <div key={key} className="bg-white border border-[#E4E4E7] rounded-md p-6">
                <h2 className="text-xl font-bold mb-4">{label}</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={contents[key]?.title || ''}
                      onChange={(e) => updateContent(key, 'title', e.target.value)}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>Conteúdo</Label>
                    <Textarea
                      value={contents[key]?.content || ''}
                      onChange={(e) => updateContent(key, 'content', e.target.value)}
                      rows={8}
                      className="mt-2"
                      placeholder="Use \\n para quebras de linha"
                    />
                  </div>

                  <Button
                    onClick={() => handleUpdate(key)}
                    disabled={saving === key}
                    className="bg-[#002FA7] hover:bg-[#001f75]"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving === key ? 'Salvando...' : 'Salvar Alterações'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContentPage;
